import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import { DataTableFilterKind } from './DataTable.types';
import {
    buildServerLoadOptions,
    IServerGroup,
    IServerLoadOptions,
    ServerLoader,
} from './dataTableServerSource';

/**
 * Серверная группировка (DX-протокол, как у старого грида с groupPaging):
 * - список — это страницы СВЁРНУТЫХ групп: запрос с `group: [уровень]`,
 *   `requireGroupCount: true` и skip/take по группам; ответ — `{key, caption,
 *   count}` без элементов;
 * - раскрытие группы лениво грузит содержимое: следующий уровень групп (при
 *   многоуровневой группировке) или страницы строк — обычный плоский запрос с
 *   фильтром по ключам родителей;
 * - счётчики групп честные (с сервера), строки группы приходят подряд, догрузка
 *   ничего не вставляет выше вьюпорта — скролл не прыгает;
 * - в постраничном режиме (pageIndex) страница — это groupsPageSize групп верхнего
 *   уровня: пейджер листает группы, а содержимое раскрытой группы догружается её
 *   «Показать ещё», как и при прокрутке. Раскрытие не сдвигает группы между страницами.
 *
 * Хук отдаёт плоский список записей (группы, строки, кнопки «Показать ещё») и
 * серверное состояние, совместимое с useDataTableServerData, — таблица работает
 * с ним как с обычным серверным источником.
 */

/** Строк в одной порции содержимого раскрытой группы. */
const GROUP_ROWS_PAGE = 100;

export type ServerGroupEntry<T> =
    | {
          kind: 'group';
          id: string;
          path: unknown[];
          depth: number;
          columnId: string;
          caption: string;
          count: number;
          expanded: boolean;
          loading: boolean;
      }
    | { kind: 'row'; id: string; row: T }
    | {
          kind: 'more';
          id: string;
          path: unknown[];
          depth: number;
          /** Сколько групп/строк ещё не загружено. */
          remaining: number;
          /** Кнопка догружает группы (иначе — строки). */
          forGroups: boolean;
          loading: boolean;
          error: boolean;
      };

/** Содержимое одного узла: у промежуточных уровней — группы, у листовых — строки. */
interface INodeState<T> {
    groups?: IServerGroup[];
    groupCount?: number;
    rows?: T[];
    rowCount?: number;
    loading: boolean;
    error: boolean;
}

interface IUseServerGroupsArgs<T> {
    enabled: boolean;
    load?: ServerLoader<T>;
    grouping: string[];
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    globalFilter?: string;
    searchFields?: string[];
    filterKinds?: Record<string, DataTableFilterKind>;
    refreshToken?: number;
    /** Другая выборка (новый загрузчик, другой набор записей): группы читаются заново,
     *  раскрытые пути сбрасываются — к новой выборке они отношения не имеют. */
    resetToken?: number;
    /** Групп в одной странице (страница списка). */
    groupsPageSize: number;
    /** Постраничный режим: номер страницы групп верхнего уровня. Без него группы
     *  верхнего уровня копятся догрузкой (loadMore) — режим прокрутки. */
    pageIndex?: number;
}

export interface IServerGroupsState<T> {
    entries: ServerGroupEntry<T>[];
    /** Всего строк в выборке (totalCount группового ответа). */
    totalCount: number;
    /** Всего групп верхнего уровня. */
    groupCount: number;
    /** Групп верхнего уровня загружено (постранично — на текущей странице). */
    loadedGroupCount: number;
    loading: boolean;
    error: unknown;
    errorOnLoadMore: boolean;
    hasMore: boolean;
    loadMore?: () => void;
    reload: () => void;
    /** Параметры плоской выборки (без group) — для экспорта и «выбрать все». */
    flatLoadOptions: IServerLoadOptions;
    toggleGroup: (path: unknown[]) => void;
    loadMoreAt: (path: unknown[]) => void;
    /** Раскрыта хотя бы одна группа. */
    anyExpanded: boolean;
    /** «Развернуть все» доступно: групп верхнего уровня в выборке не больше лимита — каждая раскрытая грузит
     *  своё содержимое отдельным запросом, а догруженные позже группы пришли бы свёрнутыми. */
    canExpandAll: boolean;
    /** Свернуть все группы или раскрыть загруженные группы верхнего уровня (вложенные — по одной). */
    setAllExpanded: (open: boolean) => void;
}

/** Больше групп разом не раскрываем: каждая — отдельный запрос содержимого. */
export const SERVER_EXPAND_ALL_LIMIT = 20;

const pathKeyOf = (path: unknown[]): string => JSON.stringify(path);
const ROOT_KEY = pathKeyOf([]);

export function useDataTableServerGroups<T>({
    enabled,
    load,
    grouping,
    sorting,
    columnFilters,
    globalFilter,
    searchFields,
    filterKinds,
    refreshToken = 0,
    resetToken = 0,
    groupsPageSize,
    pageIndex,
}: IUseServerGroupsArgs<T>): IServerGroupsState<T> {
    const paged = pageIndex !== undefined;
    const [nodes, setNodes] = useState<Map<string, INodeState<T>>>(() => new Map());
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
    // зеркало для чтений вне рендера (loadNode): setState-апдейтер для чтения
    // был бы сайд-эффектом и в StrictMode дублировал бы запросы
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    // Поколение выборки: растёт на каждый сброс, ответы прежнего поколения игнорируются.
    const generationRef = useRef(0);
    const controllersRef = useRef(new Set<AbortController>());
    const loadRef = useRef(load);
    loadRef.current = load;
    const filterKindsRef = useRef(filterKinds);
    filterKindsRef.current = filterKinds;

    const groupingKey = JSON.stringify(grouping);
    const sortingKey = JSON.stringify(sorting);
    const filtersKey = JSON.stringify(columnFilters);
    const searchFieldsKey = JSON.stringify(searchFields);

    /** Базовые параметры плоской выборки (фильтры, поиск, сортировка). */
    const flatLoadOptions = useMemo(
        () =>
            buildServerLoadOptions({
                pageIndex: 0,
                pageSize: groupsPageSize,
                sorting,
                columnFilters,
                globalFilter,
                searchFields,
                filterKinds: filterKindsRef.current,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sortingKey, filtersKey, globalFilter, searchFieldsKey, groupsPageSize],
    );

    /** Фильтр по ключам родителей: строки/подгруппы только этой ветки. */
    const withPathFilter = useCallback(
        (options: IServerLoadOptions, path: unknown[]): IServerLoadOptions => {
            if (!path.length) return options;
            const parts: unknown[] = [];
            path.forEach((key, level) => {
                if (level) parts.push('and');
                parts.push([grouping[level], '=', key]);
            });
            const pathFilter = parts.length === 1 ? parts[0] : parts;
            return {
                ...options,
                filter: options.filter ? [options.filter, 'and', pathFilter] : pathFilter,
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [groupingKey],
    );

    const updateNode = useCallback((key: string, patch: Partial<INodeState<T>>) => {
        setNodes((prev) => {
            const next = new Map(prev);
            const current = next.get(key) ?? { loading: false, error: false };
            next.set(key, { ...current, ...patch });
            return next;
        });
    }, []);

    /** Загрузка содержимого узла: страница групп уровня или страница строк. */
    const loadNode = useCallback(
        (path: unknown[]) => {
            const loader = loadRef.current;
            if (!loader) return;
            // Путь глубже текущей группировки остался от прежней: его уровней в grouping нет,
            // и фильтр ветки собрался бы с `undefined` в качестве поля (сервер такой не разберёт).
            // Эффект сброса чистит expanded, но эффекты одного коммита видят его прежнее значение.
            if (path.length > grouping.length) return;
            const key = pathKeyOf(path);
            const node = nodesRef.current.get(key);
            if (node?.loading) return;
            const generation = generationRef.current;
            const depth = path.length;
            const groupsLevel = depth < grouping.length;
            const loaded = groupsLevel ? (node?.groups?.length ?? 0) : (node?.rows?.length ?? 0);
            updateNode(key, { loading: true, error: false });
            const base = withPathFilter(flatLoadOptions, path);
            const options: IServerLoadOptions = groupsLevel
                ? {
                      ...base,
                      skip: (depth === 0 ? (pageIndex ?? 0) * groupsPageSize : 0) + loaded,
                      take: groupsPageSize,
                      group: [
                          {
                              selector: grouping[depth],
                              // направление групп = сортировка их колонки (как в DX)
                              desc: sorting.find((s) => s.id === grouping[depth])?.desc ?? false,
                              isExpanded: false,
                          },
                      ],
                      requireGroupCount: true,
                      scope: { kind: 'groups', path },
                  }
                : { ...base, skip: loaded, take: GROUP_ROWS_PAGE, scope: { kind: 'groupRows', path } };
            const controller = new AbortController();
            controllersRef.current.add(controller);
            loader(options, controller.signal)
                .then((result) => {
                    if (generation !== generationRef.current) return;
                    setNodes((current) => {
                        const next = new Map(current);
                        const existing = next.get(key) ?? { loading: false, error: false };
                        next.set(
                            key,
                            groupsLevel
                                ? {
                                      ...existing,
                                      groups: [...(existing.groups ?? []), ...(result.groups ?? [])],
                                      groupCount: result.groupCount ?? result.groups?.length ?? 0,
                                      // totalCount группового ответа = строк во всей ветке
                                      rowCount: result.totalCount,
                                      loading: false,
                                      error: false,
                                  }
                                : {
                                      ...existing,
                                      rows: [...(existing.rows ?? []), ...result.rows],
                                      rowCount: result.totalCount,
                                      loading: false,
                                      error: false,
                                  },
                        );
                        return next;
                    });
                })
                .catch(() => {
                    if (generation !== generationRef.current || controller.signal.aborted) return;
                    updateNode(key, { loading: false, error: true });
                })
                .finally(() => {
                    controllersRef.current.delete(controller);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [flatLoadOptions, withPathFilter, groupingKey, sortingKey, groupsPageSize, pageIndex, updateNode],
    );

    const reload = useCallback(() => {
        generationRef.current += 1;
        controllersRef.current.forEach((c) => c.abort());
        controllersRef.current.clear();
        setNodes(new Map());
    }, []);

    // Сброс выборки: смена группировки/сортировки/фильтров/поиска, страницы или refreshToken.
    // Раскрытые пути сохраняются — их содержимое перечитает автозагрузка ниже.
    //
    // Исключение — смена самой группировки: ключ раскрытого пути это значения по её уровням,
    // и к новой группировке прежние пути отношения не имеют. Лишний уровень дал бы запрос с
    // фильтром по несуществующему полю, а случайно совпавший ключ раскрыл бы постороннюю группу.
    const prevGroupingKeyRef = useRef(groupingKey);
    const prevResetTokenRef = useRef(resetToken);
    useEffect(() => {
        // Выключенный хук (таблица без серверной группировки) пустое состояние не трогает: новый Map —
        // новое состояние и лишний рендер всей таблицы на каждую смену страницы, сортировки, фильтра.
        if (enabled || nodesRef.current.size) reload();
        if (prevGroupingKeyRef.current !== groupingKey || prevResetTokenRef.current !== resetToken) {
            prevGroupingKeyRef.current = groupingKey;
            prevResetTokenRef.current = resetToken;
            setExpanded((prev) => (prev.size ? new Set<string>() : prev));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        enabled,
        groupingKey,
        sortingKey,
        filtersKey,
        globalFilter,
        searchFieldsKey,
        refreshToken,
        resetToken,
        pageIndex,
        // другой размер страницы групп — другие границы страниц (на первой странице pageIndex не меняется)
        paged ? groupsPageSize : 0,
    ]);

    // Автозагрузка: корень и каждая раскрытая группа без содержимого. Так один
    // механизм покрывает и первый запрос, и перечитывание после сброса.
    useEffect(() => {
        if (!enabled || !grouping.length) return;
        const missing: unknown[][] = [];
        const rootNode = nodes.get(ROOT_KEY);
        if (!rootNode) missing.push([]);
        // Постранично раскрытые группы других страниц остаются раскрытыми до возврата на
        // них, но их содержимое не читается: группа должна быть на текущей странице.
        const pageKeys = paged ? new Set(rootNode?.groups?.map((group) => pathKeyOf([group.key]))) : undefined;
        expanded.forEach((key) => {
            const path = JSON.parse(key) as unknown[];
            // родительская ветка должна быть видима (все предки раскрыты и загружены)
            for (let level = 1; level < path.length; level++) {
                if (!expanded.has(pathKeyOf(path.slice(0, level)))) return;
            }
            if (pageKeys && !pageKeys.has(pathKeyOf(path.slice(0, 1)))) return;
            if (!nodes.get(key)) missing.push(path);
        });
        missing.forEach((path) => loadNode(path));
    }, [enabled, nodes, expanded, loadNode, grouping.length, paged]);

    const toggleGroup = useCallback((path: unknown[]) => {
        const key = pathKeyOf(path);
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const loadMoreAt = useCallback((path: unknown[]) => loadNode(path), [loadNode]);

    const rootGroups = nodes.get(ROOT_KEY)?.groups;
    const setAllExpanded = useCallback(
        (open: boolean) => {
            if (!open) {
                setExpanded(new Set());
                return;
            }
            const top = rootGroups ?? [];
            if (top.length > SERVER_EXPAND_ALL_LIMIT) return;
            setExpanded((prev) => {
                const next = new Set(prev);
                top.forEach((group) => next.add(pathKeyOf([group.key])));
                return next;
            });
        },
        [rootGroups],
    );

    // ----- Плоский список записей -----
    const entries = useMemo<ServerGroupEntry<T>[]>(() => {
        if (!enabled || !grouping.length) return [];
        const out: ServerGroupEntry<T>[] = [];
        const walk = (path: unknown[], depth: number) => {
            const key = pathKeyOf(path);
            const node = nodes.get(key);
            if (!node) return;
            if (depth < grouping.length) {
                const groups = node.groups ?? [];
                groups.forEach((group) => {
                    const groupPath = [...path, group.key];
                    const groupKey = pathKeyOf(groupPath);
                    const isOpen = expanded.has(groupKey);
                    const child = nodes.get(groupKey);
                    out.push({
                        kind: 'group',
                        id: `g:${groupKey}`,
                        path: groupPath,
                        depth,
                        columnId: grouping[depth],
                        caption: group.caption ?? (group.key == null ? '' : String(group.key)),
                        count: group.count,
                        expanded: isOpen,
                        loading: isOpen && !!child?.loading && !child.groups && !child.rows,
                    });
                    if (isOpen) walk(groupPath, depth + 1);
                });
                const total = node.groupCount ?? groups.length;
                const remaining = Math.max(0, total - groups.length);
                // у корня продолжение грузит сентинел прокрутки (постранично — пейджер),
                // кнопку не рисуем
                if (depth > 0 && (remaining > 0 || node.error)) {
                    out.push({
                        kind: 'more',
                        id: `m:${key}`,
                        path,
                        depth,
                        remaining,
                        forGroups: true,
                        loading: node.loading,
                        error: node.error,
                    });
                }
            } else {
                const rows = node.rows ?? [];
                rows.forEach((row, index) => out.push({ kind: 'row', id: `${key}#${index}`, row }));
                const total = node.rowCount ?? rows.length;
                const remaining = Math.max(0, total - rows.length);
                if (remaining > 0 || node.error) {
                    out.push({
                        kind: 'more',
                        id: `m:${key}`,
                        path,
                        depth,
                        remaining,
                        forGroups: false,
                        loading: node.loading,
                        error: node.error,
                    });
                }
            }
        };
        walk([], 0);
        return out;
    }, [enabled, nodes, expanded, grouping]);

    const root = nodes.get(ROOT_KEY);
    const loadedGroupCount = root?.groups?.length ?? 0;
    // постранично кнопка свёртки относится к группам текущей страницы: раскрытые на других не в счёт
    const anyExpanded = paged ? !!rootGroups?.some((group) => expanded.has(pathKeyOf([group.key]))) : expanded.size > 0;
    const groupCount = root?.groupCount ?? 0;
    // постранично следующие группы верхнего уровня — следующая страница, а не догрузка
    const hasMore = !paged && !!root && loadedGroupCount < groupCount;
    const rootLoading = !!root?.loading;
    const loadMore = useCallback(() => loadNode([]), [loadNode]);

    return {
        entries,
        totalCount: root?.rowCount ?? 0,
        groupCount,
        loadedGroupCount,
        // включая догрузку страницы групп: полноэкранность прелоадера таблица
        // решает сама по наличию уже показанных строк
        loading: rootLoading || !root,
        error: !rootLoading && root?.error && loadedGroupCount === 0 ? new Error('groups load failed') : undefined,
        errorOnLoadMore: !!root?.error && loadedGroupCount > 0,
        hasMore,
        loadMore: hasMore && !rootLoading && !root?.error ? loadMore : undefined,
        reload,
        flatLoadOptions,
        toggleGroup,
        loadMoreAt,
        anyExpanded,
        canExpandAll: (paged ? loadedGroupCount : groupCount) <= SERVER_EXPAND_ALL_LIMIT,
        setAllExpanded,
    };
}
